import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  ChevronRight,
  ClipboardCheck,
  ClipboardPenLine,
} from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { listMyOrders } from "../../entities/order/api";
import { getApiErrorMessage } from "../../shared/api/errors";

const statusLabels = {
  PENDING_AUDIT: "审核中",
  WAITING_PAYMENT: "待支付",
  OPEN_FOR_GRAB: "待接单",
  PROVIDER_PENDING_CONFIRM: "待确认",
  IN_SERVICE: "服务中",
  DELIVERED: "待验收",
  IN_REVISION: "修改中",
  IN_DISPUTE: "争议中",
  COMPLETED: "已完成",
  CLOSED: "已关闭",
};
const TERMINAL_ORDER_STATUSES = ["COMPLETED", "CLOSED"];

const views = {
  sent: {
    role: "demand",
    label: "我的发单",
    empty: "暂时还没有发布过设计需求",
    roleLabel: "我的需求",
    icon: ClipboardPenLine,
  },
  received: {
    role: "provider",
    label: "我的接单",
    empty: "暂时还没有承接的设计服务",
    roleLabel: "设计服务",
    icon: ClipboardCheck,
  },
};

function normalizeOrder(order, view) {
  const status = order.order_status || "UNKNOWN";
  return {
    id: order.id,
    orderNo: order.order_no || order.id,
    title: order.title || "未命名需求",
    amount: order.amount_yuan || "0.00",
    status,
    statusLabel: statusLabels[status] || "处理中",
    role: views[view].roleLabel,
    date: order.created_at
      ? new Date(order.created_at).toLocaleDateString("zh-CN")
      : "—",
  };
}

export default function OrdersPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const requestedView = searchParams.get("view");
  const activeView = requestedView === "received" ? "received" : "sent";
  const viewConfig = views[activeView];
  const EmptyIcon = viewConfig.icon;
  const [activeFilter, setActiveFilter] = useState("全部");
  const ordersQuery = useQuery({
    queryKey: ["orders", "mine", viewConfig.role],
    queryFn: () => listMyOrders({ role: viewConfig.role }),
  });
  const sourceOrders = useMemo(
    () => (ordersQuery.data?.items || []).map((order) => normalizeOrder(order, activeView)),
    [activeView, ordersQuery.data],
  );
  const filteredOrders = useMemo(() => {
    if (activeFilter === "全部") return sourceOrders;
    if (activeFilter === "进行中") {
      return sourceOrders.filter((order) => !TERMINAL_ORDER_STATUSES.includes(order.status));
    }
    return sourceOrders.filter((order) => TERMINAL_ORDER_STATUSES.includes(order.status));
  }, [activeFilter, sourceOrders]);

  const inProgressCount = sourceOrders.filter(
    (order) => !TERMINAL_ORDER_STATUSES.includes(order.status),
  ).length;
  const completedCount = sourceOrders.filter(
    (order) => TERMINAL_ORDER_STATUSES.includes(order.status),
  ).length;

  function changeView(view) {
    setActiveFilter("全部");
    setSearchParams({ view });
  }

  return (
    <section className="page-container orders-page">
      <nav className="order-view-tabs" aria-label="订单类型">
        {Object.entries(views).map(([view, config]) => {
          const Icon = config.icon;
          return (
            <button
              key={view}
              type="button"
              className={activeView === view ? "active" : ""}
              aria-pressed={activeView === view}
              onClick={() => changeView(view)}
            >
              <Icon size={18} />
              <span>
                <strong>{config.label}</strong>
                <small>{view === "sent" ? "我发布的需求" : "我承接的服务"}</small>
              </span>
            </button>
          );
        })}
      </nav>

      {ordersQuery.isPending ? (
        <div className="detail-state order-page-state">正在加载订单……</div>
      ) : null}
      {ordersQuery.isError ? (
        <div className="detail-state error order-page-state">
          {getApiErrorMessage(ordersQuery.error, "订单加载失败")}
        </div>
      ) : null}
      {ordersQuery.isSuccess ? (
        <>
          <div className="order-summary">
            <div>
              <span>全部订单</span>
              <strong>{sourceOrders.length}</strong>
              <small>{activeView === "sent" ? "已发布的设计需求" : "已承接的设计服务"}</small>
            </div>
            <div>
              <span>进行中</span>
              <strong>{inProgressCount}</strong>
              <small>当前需要关注的订单</small>
            </div>
            <div>
              <span>已完成</span>
              <strong>{completedCount}</strong>
              <small>已完成交付与验收</small>
            </div>
          </div>
          <div className="filter-tabs">
            {["全部", "进行中", "已完成"].map((filter) => (
              <button
                key={filter}
                type="button"
                className={filter === activeFilter ? "active" : ""}
                onClick={() => setActiveFilter(filter)}
              >
                {filter}
              </button>
            ))}
          </div>
          <div className="order-list">
            {filteredOrders.length ? filteredOrders.map((order) => (
              <article className="order-card" key={order.id}>
                <div className="order-card-main">
                  <span className="order-role">{order.role}</span>
                  <div>
                    <small>{order.orderNo}</small>
                    <h3>{order.title}</h3>
                    <p>创建于 {order.date}</p>
                  </div>
                </div>
                <div className="order-card-status">
                  <span className={`status ${order.status.toLowerCase()}`}>
                    {order.statusLabel}
                  </span>
                  <strong>¥{order.amount}</strong>
                  <button type="button" onClick={() => navigate(`/orders/${order.id}`)}>
                    查看详情 <ChevronRight size={15} />
                  </button>
                </div>
              </article>
            )) : (
              <div className="orders-empty-state">
                <EmptyIcon size={28} />
                <strong>{activeFilter === "全部" ? viewConfig.empty : `没有${activeFilter}订单`}</strong>
                <p>{activeView === "sent" ? "可以从创建页面发布一条新的设计需求。" : "承接服务后，订单会显示在这里。"}</p>
              </div>
            )}
          </div>
        </>
      ) : null}
    </section>
  );
}
